import React, { FC } from 'react';
import { SummaryRow } from '../styles/summary-styles';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { parsePrice } from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import moment from 'moment';
import styled from 'styled-components';
import { Text } from 'components/text/Text';
import { SeekerSubscriptionChangeInfoResponse } from 'logic/api-models/api-models';
import { colors } from 'styles/colors';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getElitePaymentConfig } from 'logic/store/elitetutor/elitetutor.selectors';

const REMAINING_VALID_WARN_THRESHOLD_SECONDS = 300; // To display correctly < 1 hour

const PreviewBox = styled.div``;

const PreviewInfoBox = styled.div`
  margin: 10px 0;
`;

const StyledText = styled(Text)`
  display: block;
`;

const TimeoutSummaryRow = styled(SummaryRow)`
  margin-top: 30px;
`;

const TimeoutStyledText = styled(StyledText)`
  color: ${colors.red};
`;

export const SubChangePreview: FC<{
  subChangeInfo?: SeekerSubscriptionChangeInfoResponse | null;
  remainingValidSeconds?: number | null;
  confirmPreview?: boolean;
}> = ({ subChangeInfo, confirmPreview, remainingValidSeconds }) => {
  const elitePaymentConfig = useSelector(getElitePaymentConfig);
  const totalBillingCycles =
    elitePaymentConfig?.plans.find((plan) => plan.code === subChangeInfo?.plan?.code)
      ?.totalBillingCycles ?? translate(translationKeys.errors.error);
  const planName = subChangeInfo?.plan?.name || translate(translationKeys.errors.error);
  const remainingValidDuration =
    remainingValidSeconds != null
      ? moment.duration(remainingValidSeconds, 'seconds')
      : remainingValidSeconds;
  if (!subChangeInfo) return null;
  if (confirmPreview)
    return (
      <PreviewBox>
        <SummaryRow>
          <StyledText>
            {translate(translationKeys.pricing.subscriptionChangePreview.confirmPreviewText, {
              date: moment(subChangeInfo?.activateAt).format('DD.MM.YYYY'),
              lessonUnits: subChangeInfo?.credit,
              price: parsePrice(subChangeInfo?.guessedSubtotal),
              subscriptionName: subChangeInfo?.plan?.name,
              totalBillingCycles,
            })}
          </StyledText>
        </SummaryRow>
        {remainingValidSeconds != null &&
          remainingValidSeconds < REMAINING_VALID_WARN_THRESHOLD_SECONDS && (
            <TimeoutSummaryRow>
              <TimeoutStyledText>
                {remainingValidSeconds >= 0
                  ? translate(
                      translationKeys.pricing.subscriptionChangePreview.timeoutWarningText,
                      {
                        remainingMinutes: remainingValidDuration?.minutes(),
                        remainingSeconds: remainingValidDuration?.seconds(),
                      }
                    )
                  : translate(translationKeys.pricing.subscriptionChangePreview.timeoutErrorText)}
              </TimeoutStyledText>
            </TimeoutSummaryRow>
          )}
      </PreviewBox>
    );
  return (
    <PreviewBox>
      <SummaryRow>
        <StyledText>
          {translate(translationKeys.pricing.subscriptionChangePreview.text, {
            date: moment(subChangeInfo?.activateAt).format('DD.MM.YYYY'),
            lessonUnits: subChangeInfo?.credit,
            price: parsePrice(subChangeInfo?.guessedSubtotal),
            subscriptionName: subChangeInfo?.plan?.name,
          })}{' '}
          {typeof totalBillingCycles === 'number'
            ? translate(translationKeys.pricing.subscriptionChangePreview.textBillingCycles, {
                totalBillingCycles,
              })
            : translate(translationKeys.pricing.subscriptionChangePreview.textNoBillingCycles, {
                planName,
              })}
        </StyledText>
      </SummaryRow>
    </PreviewBox>
  );
};

export const SubChangePreviewInfoBox: FC<{
  subChangeInfo?: SeekerSubscriptionChangeInfoResponse | null;
}> = ({ subChangeInfo }) => {
  if (!subChangeInfo) return null;

  return (
    <PreviewInfoBox>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <InfoCircleOutlined style={{ color: '#faad14', margin: '5px 5px 5px 0' }} />
        <div>
          <Text size={14} fontWeight={700} style={{ display: 'block' }}>
            {translate(translationKeys.pricing.subscriptionChangePreview.header)}
          </Text>
        </div>
      </div>
      <SubChangePreview subChangeInfo={subChangeInfo} />
    </PreviewInfoBox>
  );
};
